import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type { TGetNarrativeDev } from '../../modules/claims/freeClaims/narrativeDevTool/useGetGptResponse';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { makeAPIRequestReactQueryWithHeaders } from '../api';
import { endpoints } from '../endpoints';

export interface InsuranceNarrativeResult {
  narrative: string;
  cached: boolean;
  devToolData: TGetNarrativeDev | undefined;
  traceId?: string; // Backend trace ID for Langfuse linking
}

export const useGetNarrative = (
  estimatedVisitId: number,
  treatmentIds: number[],
  unifiedAppointmentId?: number,
  options?: UseQueryOptions<
    InsuranceNarrativeResult,
    unknown,
    InsuranceNarrativeResult
  >,
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  const { debouncedValue: debouncedInput } = useDebounce(treatmentIds, 1000);
  const path = `${endpoints.v2}/${endpoints.narrative}/${endpoints.insuranceSummary}`;

  return useQuery<InsuranceNarrativeResult>(
    [debouncedInput, currentOrg, currentPractice, estimatedVisitId],
    () =>
      makeAPIRequestReactQueryWithHeaders<InsuranceNarrativeResult>(
        path,
        undefined,
        {
          Org: currentOrg,
          Practice: currentPractice,
        },
        'POST',
        {
          data: {
            estimatedVisitId: Number(estimatedVisitId),
            unifiedAppointmentId,
            treatmentIds,
          },
        },
      ),
    options,
  );
};
